import { useMemo } from "react";
import {
  type HotkeyCallback,
  type Keys,
  useHotkeys as useHotkeysOriginal,
} from "react-hotkeys-hook";
import type { OptionsOrDependencyArray } from "react-hotkeys-hook/dist/types";

import { useFrameDocument } from "./useFrameDocument";

/**
 * A wrapper around react-hotkeys-hook's useHotkeys that automatically
 * uses the frame document when available (e.g., in Picture-in-Picture windows).
 * Falls back to the regular document if not in a frame context.
 *
 * This hook maintains the same API as useHotkeys and can be used as a drop-in replacement.
 *
 * @param keys - The hotkey or hotkeys to listen for
 * @param callback - The callback function to execute when the hotkey is pressed
 * @param options - Options for the hotkey hook
 * @param deps - Dependency array for the callback (like useCallback)
 */
export const useFrameHotkeys = (
  keys: Keys,
  callback: HotkeyCallback,
  options?: OptionsOrDependencyArray,
  deps?: OptionsOrDependencyArray,
) => {
  const { frameDocument, isInFrame } = useFrameDocument();

  const optionsWithDocument = useMemo(() => {
    if (isInFrame) {
      return {
        ...options,
        document: frameDocument,
      };
    }
    return options;
  }, [options, frameDocument, isInFrame]);

  return useHotkeysOriginal(keys, callback, optionsWithDocument, deps);
};
